/**
 * Buyer records: the seller previews exactly what goes, shares a frozen packet by link, and sends
 * corrections as new versions. A buyer on Hondo opens the link from their ranch, matches identities
 * and adds the cattle with their history. Money, private notes and prices never travel.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "../../../web/lib/api";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi, useQuery } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { RefPicker } from "../../../web/forms/pickers";
import { Page } from "../../../web/shell/Layout";
import { Button, Card, Chip, Disclosure, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Row, Section, Segmented, TextInput, cx, toast } from "../../../web/ui/kit";
import { ID_SCHEME_LABELS, MATCH_REASON_LABELS, PEDIGREE_LABELS } from "../shared/labels";
import { PACKET_CATEGORY_LABELS, type PacketAnimal, type PacketCategory, type PacketChange, type PacketDTO, type PacketSnapshot, type ReceivePreview, type ReceiveResult } from "../shared/types";
import { act, copyLink, many, post } from "./common";

const ALL: PacketCategory[] = ["identity", "pedigree", "health", "performance", "genetics", "breeding", "sale"];

/** The readable packet: who the animals are, where they come from, and the records the seller chose. */
export function PacketView({ snapshot }: { snapshot: PacketSnapshot }) {
  return (
    <div className="sales-packet">
      {snapshot.animals.map((a) => <PacketAnimalView key={a.key} a={a} />)}
      <p className="muted sales-packet-foot">{t("Records as kept by {{ranch}}. Prices, money and private notes are not included.", { ranch: snapshot.from_ranch })}</p>
    </div>
  );
}

function PacketAnimalView({ a }: { a: PacketAnimal }) {
  const byCategory = new Map<PacketCategory, PacketAnimal["records"]>();
  for (const r of a.records) byCategory.set(r.category, [...(byCategory.get(r.category) ?? []), r]);
  return (
    <Card className="sales-packet-animal">
      <div className="sales-packet-head">
        <strong className="mono">{a.tag ?? ""}</strong>{a.name && <span>{a.name}</span>}
        <span className="muted">{[t(a.sex === "male" ? (a.castrated ? "Male, castrated" : "Male") : a.sex === "female" ? "Female" : "Unknown sex"), a.birth_date ? t("born {{date}}", { date: formatDate(a.birth_date, a.birth_precision ?? "date") }) : null, a.breed, a.color].filter(Boolean).join(" · ")}</span>
      </div>
      {a.identifiers.length > 0 && <dl className="sales-kvs">{a.identifiers.map((i, n) => <div key={n} className="sales-kv"><dt>{t(ID_SCHEME_LABELS[i.scheme] ?? i.scheme)}</dt><dd className="mono">{i.value}{i.issuer ? ` · ${i.issuer}` : ""}</dd></div>)}</dl>}
      {Object.keys(a.pedigree).length > 0 && (
        <dl className="sales-cat-pedigree">
          {(Object.entries(PEDIGREE_LABELS) as Array<[keyof PacketAnimal["pedigree"], string]>).filter(([k]) => a.pedigree[k]).map(([k, label]) => (
            <div key={k} className={`ped-${k}`}><dt>{t(label)}</dt><dd>{a.pedigree[k]!.label}{a.pedigree[k]!.registration ? <span className="muted"> · {a.pedigree[k]!.registration}</span> : null}</dd></div>
          ))}
        </dl>
      )}
      {a.sale && (
        <div className="sales-packet-sale">
          <strong>{t("Sale")}</strong>
          <span>{[a.sale.offering, a.sale.lot ? t("Lot {{lot}}", { lot: a.sale.lot }) : null, a.sale.delivered_on ? t("Delivered {{date}}", { date: formatDate(a.sale.delivered_on) }) : null].filter(Boolean).join(" · ")}</span>
          {a.sale.guarantee && <span>{t("Guarantee: {{what}}", { what: a.sale.guarantee })}</span>}
          {a.sale.retained && <span>{t("Seller keeps: {{what}}", { what: a.sale.retained })}</span>}
          {a.sale.conditions && <span>{t("Conditions: {{what}}", { what: a.sale.conditions })}</span>}
        </div>
      )}
      {[...byCategory.entries()].map(([cat, recs]) => (
        <Disclosure key={cat} label={t(PACKET_CATEGORY_LABELS[cat])} count={recs.length} defaultOpen={recs.length <= 4}>
          <ul className="sales-packet-records">{[...recs].reverse().map((r) => <li key={r.key}><span className="muted">{r.occurred_at ? formatDate(r.occurred_at, r.precision) : t("Unknown date")}</span><span>{r.summary || r.label}</span></li>)}</ul>
        </Disclosure>
      ))}
    </Card>
  );
}

function ChangeLines({ changes }: { changes: PacketChange[] }) {
  return (
    <ul className="sales-changes">
      {changes.map((c, i) => <li key={i}><span className="sales-change-where">{c.animal}</span><span>{c.change === "added" ? t("Added: {{what}}", { what: c.label }) : c.change === "removed" ? t("Removed: {{what}}", { what: c.label }) : t("Changed: {{what}}", { what: c.label })}</span></li>)}
    </ul>
  );
}

// ---------------------------------------------------------------- seller

export function NewPacketPage() {
  useLanguage();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [animals, setAnimals] = useState<string[]>(params.get("animals")?.split(",").filter(Boolean) ?? []);
  const [recipient, setRecipient] = useState<string | null>(params.get("buyer"));
  const [categories, setCategories] = useState<PacketCategory[]>(["identity", "pedigree", "health", "performance", "genetics", "sale"]);
  const [language, setLanguage] = useState<"en" | "es">("en");
  const [purpose, setPurpose] = useState("");
  const [preview, setPreview] = useState<PacketSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const key = useMemo(() => `${animals.join(",")}|${categories.join(",")}`, [animals, categories]);
  useEffect(() => {
    setPreview(null);
    if (!animals.length || !categories.length) return;
    let alive = true;
    const h = setTimeout(() => { post<PacketSnapshot>("/packets/preview", { animal_ids: animals, categories }).then((s) => { if (alive) setPreview(s); }).catch(() => {}); }, 250);
    return () => { alive = false; clearTimeout(h); };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const share = async () => {
    setBusy(true);
    try {
      const { id } = await post<{ id: string }>("/packets", { animal_ids: animals, recipient_contact_id: recipient, categories, language, purpose: purpose || null, offering_id: params.get("offering") });
      await invalidateAll();
      toast(t("Records shared"), { tone: "good" });
      navigate(`/work/sales/packets/${id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  const count = preview?.animals.reduce((n, a) => n + a.records.length, 0) ?? 0;
  return (
    <Page>
      <PageHeader back={{ to: params.get("offering") ? `/work/jobs/${params.get("offering")}?tab=fulfillment` : "/work/sales", label: t("Sales") }} title={t("Share records with a buyer")}
        subtitle={t("The buyer gets a read-only copy frozen today. You choose what goes; money and private notes never do.")} />
      <Card>
        <Field label={t("Cattle")}><RefPicker target="animal" multiple value={animals} onChange={(v) => setAnimals((v as string[]) ?? [])} /></Field>
        <Field label={t("Buyer")} optional><RefPicker target="contact" value={recipient} onChange={(v) => setRecipient(v as string | null)} /></Field>
        <Field label={t("What to share")}>
          <div className="choice-chips">
            {ALL.map((c) => <button key={c} type="button" aria-pressed={categories.includes(c)} className={cx("choice", categories.includes(c) && "choice-on")} onClick={() => setCategories(categories.includes(c) ? categories.filter((x) => x !== c) : [...categories, c])}>{t(PACKET_CATEGORY_LABELS[c])}</button>)}
          </div>
        </Field>
        <Field label={t("Buyer reads")}><Segmented label={t("Buyer reads")} value={language} onChange={setLanguage} options={[{ value: "en", label: "English" }, { value: "es", label: "Español" }]} /></Field>
        <Field label={t("Note for the buyer")} optional><TextInput value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder={t("Records for the bull you bought")} /></Field>
      </Card>
      {preview && (
        <Section title={t("What the buyer will see")} hint={plural(count, "{{count}} record", "{{count}} records")}>
          <PacketView snapshot={preview} />
        </Section>
      )}
      <div className="form-actions"><Button variant="primary" size="lg" icon="share" busy={busy} disabled={!animals.length || !categories.length} onClick={() => void share()}>{t("Share these records")}</Button></div>
    </Page>
  );
}

export function PacketPage() {
  useLanguage();
  const { packetId } = useParams();
  const navigate = useNavigate();
  const q = useApi<PacketDTO>(`/m/sales/packets/${packetId}`);
  const [changes, setChanges] = useState<PacketChange[] | null>(null);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const p = q.data;
  const link = `/work/sales/receive/${p.token}${p.language === "es" ? "?lang=es" : ""}`;
  const update = async () => {
    try {
      const r = await post<{ id: string; changes: PacketChange[] }>(`/packets/${p.id}/update`);
      await invalidateAll();
      if (!r.changes.length) { toast(t("Nothing changed since this version."), { tone: "info" }); return; }
      setChanges(r.changes);
      toast(t("New version shared"), { tone: "good" });
      navigate(`/work/sales/packets/${r.id}`, { replace: true });
    } catch (err) { toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); }
  };
  return (
    <Page>
      <PageHeader back={{ to: "/work/sales", label: t("Sales") }} eyebrow={t("Buyer records")} title={p.recipient ? t("For {{buyer}}", { buyer: p.recipient.label }) : t("Shared records")}
        subtitle={[t("Version {{version}}", { version: p.version }), formatDate(p.created_at), p.categories.map((c) => t(PACKET_CATEGORY_LABELS[c])).join(", ")].join(" · ")}>
        <div className="animal-status">{p.status === "revoked" ? <Chip tone="neutral" icon="archive">{t("Taken back")}</Chip> : p.status === "superseded" ? <Chip tone="neutral">{t("Replaced by a newer version")}</Chip> : <Chip tone="good" icon="share">{t("Shared")}</Chip>}</div>
      </PageHeader>
      {p.status === "shared" && (
        <div className="quick-actions">
          <Button variant="primary" size="lg" icon="link" onClick={() => void copyLink(link)}>{t("Copy link for the buyer")}</Button>
          <Button size="lg" icon="eye" onClick={() => void copyLink(`/records/${p.token}${p.language === "es" ? "?lang=es" : ""}`)}>{t("Copy read-only link")}</Button>
          <Button size="lg" icon="reset" onClick={() => void update()}>{t("Send corrections")}</Button>
          <Button variant="ghost" icon="archive" onClick={() => { if (confirm(t("Take these records back? The link stops working; copies the buyer already saved stay with them."))) void act(() => post(`/packets/${p.id}/revoke`), t("Records taken back")); }}>{t("Take back")}</Button>
        </div>
      )}
      {(changes ?? p.changes).length > 0 && <Section title={t("What changed in this version")}><ChangeLines changes={changes ?? p.changes} /></Section>}
      <Section title={t("What the buyer sees")}><PacketView snapshot={p.snapshot} /></Section>
      {p.versions.length > 1 && <Disclosure label={t("All versions")} count={p.versions.length}><List>{p.versions.map((v) => <Row key={v.id} icon="packet" to={`/work/sales/packets/${v.id}`} title={t("Version {{version}}", { version: v.version })} meta={formatDate(v.created_at)} selected={v.id === p.id} />)}</List></Disclosure>}
    </Page>
  );
}

// ---------------------------------------------------------------- buyer

export function ReceivePage() {
  useLanguage();
  const { token } = useParams();
  const can = useCan();
  const q = useApi<ReceivePreview>(`/m/sales/receive/${token}`, { retry: false });
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ReceiveResult | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (q.data) setChoices(Object.fromEntries(q.data.animals.map((a) => [a.key, a.imported_as?.id ?? a.suggested]))); }, [q.data]);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <Page><Empty icon="packet" title={t("This link doesn't work anymore. Ask the seller to share the records again.")} /></Page>;
  const p = q.data;
  const fresh = !p.prior || p.prior.version < p.version;
  const receive = async () => {
    setBusy(true);
    try {
      const r = await api.post<ReceiveResult>(`/m/sales/receive/${token}`, { decisions: choices });
      setResult(r);
      await invalidateAll();
      toast(t("Added to your cattle"), { tone: "good" });
    } catch (err) { toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" }); }
    finally { setBusy(false); }
  };
  return (
    <Page>
      <PageHeader back={{ to: "/cattle", label: t("Cattle") }} eyebrow={t("Records from {{ranch}}", { ranch: p.from_ranch })} title={plural(p.animals.length, "{{count}} animal", "{{count}} animals")}
        subtitle={[t("Version {{version}}", { version: p.version }), formatDate(p.created_at), p.purpose].filter(Boolean).join(" · ")} />
      {p.own_ranch && <Card className="sales-next"><p className="sales-next-title">{t("These records came from this ranch.")}</p><p className="muted">{t("Open the link while you're in the buyer's ranch to add the cattle there.")}</p></Card>}
      {p.prior && (
        <Card className="sales-next">
          <p className="sales-next-title">{fresh ? many.count(p.changes.length, "Version {{version}} has {{count}} change since you added these", "Version {{version}} has {{count}} changes since you added these", { version: p.version }) : t("You already added these on {{date}}.", { date: formatDate(p.prior.imported_at) })}</p>
          {fresh && <ChangeLines changes={p.changes} />}
          {fresh && <p className="muted">{t("Taking the changes corrects the records that came from the seller. Anything you recorded yourself stays as it is.")}</p>}
        </Card>
      )}
      {result ? (
        <Section title={t("Added")}>
          <List>{result.animals.map((a) => <Row key={a.key} icon="animal" to={`/cattle/${a.id}`} title={a.label} meta={a.created ? t("New in your cattle") : t("Matched to your animal")} />)}</List>
          <p className="muted">{t("{{added}} records added, {{changed}} corrected, {{removed}} removed.", { added: result.records.added, changed: result.records.changed, removed: result.records.removed })}</p>
        </Section>
      ) : !p.own_ranch && (
        <>
          <Section title={t("Match each animal")} hint={t("A matching EID or registration number is the same animal. A matching ear tag alone might not be.")}>
            {p.animals.map((a) => (
              <Card key={a.key} className="sales-match">
                <div className="sales-packet-head"><strong className="mono">{a.tag ?? ""}</strong>{a.name && <span>{a.name}</span>}<span className="muted">{[a.breed, a.birth_date ? formatDate(a.birth_date) : null, plural(a.records, "{{count}} record", "{{count}} records")].filter(Boolean).join(" · ")}</span></div>
                {a.imported_as ? <p><Icon name="check" size={14} /> {t("Already in your cattle as")} <Link to={`/cattle/${a.imported_as.id}`}>{a.imported_as.label}</Link></p> : (
                  <div className="sales-choice-list" role="radiogroup" aria-label={t("Match {{animal}}", { animal: a.label })}>
                    <button type="button" role="radio" aria-checked={choices[a.key] === "new"} className={cx("sales-choice", choices[a.key] === "new" && "is-on")} onClick={() => setChoices({ ...choices, [a.key]: "new" })}><strong>{t("Add as a new animal")}</strong></button>
                    {a.candidates.map((c) => (
                      <button key={c.id} type="button" role="radio" aria-checked={choices[a.key] === c.id} className={cx("sales-choice", choices[a.key] === c.id && "is-on")} onClick={() => setChoices({ ...choices, [a.key]: c.id })}>
                        <strong>{t("Same as my {{animal}}", { animal: c.label })}</strong><span className={c.strong ? "muted" : "is-overdue"}>{t(MATCH_REASON_LABELS[c.reason] ?? c.reason)}</span>
                      </button>
                    ))}
                    <button type="button" role="radio" aria-checked={choices[a.key] === "skip"} className={cx("sales-choice", choices[a.key] === "skip" && "is-on")} onClick={() => setChoices({ ...choices, [a.key]: "skip" })}><strong>{t("Leave this one out")}</strong></button>
                  </div>
                )}
              </Card>
            ))}
          </Section>
          {can("record") && (fresh || p.animals.some((a) => !a.imported_as)) && (
            <div className="form-actions"><Button variant="primary" size="lg" icon="plus" busy={busy} onClick={() => void receive()}>{p.prior && fresh ? t("Take the changes") : t("Add to my cattle")}</Button></div>
          )}
        </>
      )}
      <Section title={t("The records")}><PacketView snapshot={p.snapshot} /></Section>
    </Page>
  );
}

/** What a buyer who isn't on Hondo sees from the link: the records, read-only and printable. */
export function PublicPacket() {
  useLanguage();
  const { token = "" } = useParams();
  const q = useQuery<{ version: number; created_at: string; snapshot: PacketSnapshot }>({ queryKey: ["public-packet", token], queryFn: () => api.get(`/m/sales/public/packet/${token}`), retry: false });
  if (q.isLoading) return <Loading />;
  if (!q.data) return <div className="page"><Empty icon="packet" title={t("This link doesn't work anymore. Ask the seller to share the records again.")} /></div>;
  const s = q.data.snapshot;
  return (
    <div className="page sales-public">
      <div className="sales-print-hide sales-public-bar"><Button icon="print" onClick={() => window.print()}>{t("Print")}</Button></div>
      <header className="sales-cat-head">
        <p className="eyebrow">{t("Records from {{ranch}}", { ranch: s.from_ranch })}</p>
        <h1>{s.recipient ? t("For {{buyer}}", { buyer: s.recipient }) : t("Shared records")}</h1>
        <p className="sales-cat-version">{[t("Version {{version}}", { version: q.data.version }), formatDate(q.data.created_at), s.purpose].filter(Boolean).join(" · ")}</p>
      </header>
      <PacketView snapshot={s} />
    </div>
  );
}
